import { FileText, AlertCircle, CheckCircle, XCircle, Scale } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

const TermsOfService = () => {
  const lastUpdated = "November 28, 2024";

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
            <FileText className="w-8 h-8 text-blue-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Terms of Service</h1>
          <p className="text-gray-600">Last updated: {lastUpdated}</p>
        </div>

        {/* Introduction */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <p className="text-gray-700 leading-relaxed mb-4">
              Welcome to DiaWise! These Terms of Service ("Terms") govern your access to and use of the DiaWise diabetes management application and services. By accessing or using DiaWise, you agree to be bound by these Terms.
            </p>
            <p className="text-gray-700 leading-relaxed">
              <strong>PLEASE READ THESE TERMS CAREFULLY BEFORE USING OUR SERVICES.</strong>
            </p>
          </CardContent>
        </Card>

        {/* Acceptance of Terms */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center">
              <CheckCircle className="w-5 h-5 mr-2 text-blue-600" />
              Acceptance of Terms
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700">
              By creating an account, accessing, or using DiaWise, you acknowledge that you have read, understood, and agree to be bound by these Terms and our Privacy Policy. If you do not agree, you must not use our services.
            </p>
          </CardContent>
        </Card>

        {/* Medical Disclaimer */}
        <Card className="mb-6 border-red-200 bg-red-50">
          <CardHeader>
            <CardTitle className="flex items-center text-red-800">
              <AlertCircle className="w-5 h-5 mr-2" />
              Important Medical Disclaimer
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-red-800 font-semibold">
              DiaWise is NOT a substitute for professional medical advice, diagnosis, or treatment.
            </p>
            <ul className="space-y-2 text-red-700">
              <li className="flex items-start">
                <span className="mr-2">⚠️</span>
                <span>Always seek the advice of your physician or qualified health provider with any questions about your medical condition</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">⚠️</span>
                <span>Never disregard professional medical advice or delay seeking it because of information from DiaWise</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">⚠️</span>
                <span>DiaWise is a tool for tracking and managing diabetes, not for diagnosing or treating medical conditions</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">⚠️</span>
                <span>In case of a medical emergency, call 911 or your local emergency number immediately</span>
              </li>
            </ul>
          </CardContent>
        </Card>

        {/* Eligibility */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Eligibility</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700 mb-4">
              To use DiaWise, you must:
            </p>
            <ul className="space-y-2 text-gray-700">
              <li>• Be at least 13 years of age (or the age of legal consent in your jurisdiction)</li>
              <li>• Have the authority to enter into these Terms</li>
              <li>• Not be prohibited from using our services under applicable laws</li>
              <li>• Have a valid email address</li>
            </ul>
          </CardContent>
        </Card>

        {/* User Account */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>User Account</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Account Registration</h3>
              <p className="text-gray-700 mb-2">When you create an account, you agree to:</p>
              <ul className="space-y-1 text-gray-700">
                <li>• Provide accurate, current, and complete information</li>
                <li>• Maintain and update your information to keep it accurate</li>
                <li>• Keep your password secure and confidential</li>
                <li>• Notify us immediately of any unauthorized access</li>
                <li>• Be responsible for all activities under your account</li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Account Termination</h3>
              <p className="text-gray-700">
                We reserve the right to suspend or terminate your account if you violate these Terms or engage in fraudulent, abusive, or illegal activity.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Acceptable Use */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Scale className="w-5 h-5 mr-2 text-blue-600" />
              Acceptable Use
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700 mb-4">You agree NOT to:</p>
            <ul className="space-y-2 text-gray-700">
              <li className="flex items-start">
                <XCircle className="w-4 h-4 mr-2 text-red-600 mt-1 flex-shrink-0" />
                <span>Use the service for any illegal or unauthorized purpose</span>
              </li>
              <li className="flex items-start">
                <XCircle className="w-4 h-4 mr-2 text-red-600 mt-1 flex-shrink-0" />
                <span>Violate any laws or regulations</span>
              </li>
              <li className="flex items-start">
                <XCircle className="w-4 h-4 mr-2 text-red-600 mt-1 flex-shrink-0" />
                <span>Infringe on intellectual property rights</span>
              </li>
              <li className="flex items-start">
                <XCircle className="w-4 h-4 mr-2 text-red-600 mt-1 flex-shrink-0" />
                <span>Transmit viruses, malware, or harmful code</span>
              </li>
              <li className="flex items-start">
                <XCircle className="w-4 h-4 mr-2 text-red-600 mt-1 flex-shrink-0" />
                <span>Attempt to gain unauthorized access to our systems</span>
              </li>
              <li className="flex items-start">
                <XCircle className="w-4 h-4 mr-2 text-red-600 mt-1 flex-shrink-0" />
                <span>Impersonate others or misrepresent your affiliation</span>
              </li>
              <li className="flex items-start">
                <XCircle className="w-4 h-4 mr-2 text-red-600 mt-1 flex-shrink-0" />
                <span>Scrape, crawl, or harvest data from our services</span>
              </li>
              <li className="flex items-start">
                <XCircle className="w-4 h-4 mr-2 text-red-600 mt-1 flex-shrink-0" />
                <span>Interfere with or disrupt the service</span>
              </li>
            </ul>
          </CardContent>
        </Card>

        {/* Subscription and Payment */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Subscription and Payment</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Free and Premium Features</h3>
              <p className="text-gray-700">
                DiaWise offers both free and premium features. Premium features require a paid subscription. Subscription fees are billed in advance on a recurring basis.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Billing and Cancellation</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Subscriptions automatically renew unless cancelled</li>
                <li>• You can cancel anytime from your account settings</li>
                <li>• No refunds for partial subscription periods</li>
                <li>• We reserve the right to change pricing with 30 days' notice</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Intellectual Property */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Intellectual Property</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Our Content</h3>
              <p className="text-gray-700">
                All content, features, and functionality of DiaWise (including but not limited to text, graphics, logos, icons, images, software, and recipes) are owned by DiaWise and protected by copyright, trademark, and other intellectual property laws.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Your Content</h3>
              <p className="text-gray-700">
                You retain ownership of your health data and content you submit. By using DiaWise, you grant us a limited license to use, store, and process your content to provide our services.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Limitation of Liability */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Limitation of Liability</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700 mb-4">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW:
            </p>
            <ul className="space-y-2 text-gray-700">
              <li>• DiaWise is provided "AS IS" and "AS AVAILABLE" without warranties of any kind</li>
              <li>• We do not guarantee the accuracy, completeness, or timeliness of information</li>
              <li>• We are not liable for any damages arising from your use of our services</li>
              <li>• Our total liability shall not exceed the amount you paid us in the past 12 months</li>
              <li>• We are not responsible for third-party content or services</li>
            </ul>
          </CardContent>
        </Card>

        {/* Indemnification */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Indemnification</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700">
              You agree to indemnify, defend, and hold harmless DiaWise and its officers, directors, employees, and agents from any claims, liabilities, damages, losses, and expenses arising from your use of our services or violation of these Terms.
            </p>
          </CardContent>
        </Card>

        {/* Privacy */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Privacy</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700">
              Your privacy is important to us. Please review our <a href="/privacy-policy" className="text-blue-600 hover:underline font-semibold">Privacy Policy</a> to understand how we collect, use, and protect your personal information.
            </p>
          </CardContent>
        </Card>

        {/* Changes to Terms */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Changes to Terms</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700">
              We reserve the right to modify these Terms at any time. We will notify you of significant changes by email or through the app. Your continued use of DiaWise after changes constitutes acceptance of the new Terms.
            </p>
          </CardContent>
        </Card>

        {/* Governing Law */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Governing Law</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700">
              These Terms shall be governed by and construed in accordance with the laws of the State of California, United States, without regard to its conflict of law provisions.
            </p>
          </CardContent>
        </Card>

        {/* Contact */}
        <Card className="mb-12">
          <CardHeader>
            <CardTitle>Contact Us</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700 mb-4">
              If you have questions about these Terms, please contact us:
            </p>
            <div className="space-y-2 text-gray-700">
              <p><strong>Email:</strong> <a href="mailto:legal@diawise.app" className="text-blue-600 hover:underline">legal@diawise.app</a></p>
              <p><strong>Support:</strong> <a href="mailto:support@diawise.app" className="text-blue-600 hover:underline">support@diawise.app</a></p>
              <p><strong>Address:</strong> DiaWise Health Technologies, 123 Health Street, San Francisco, CA 94102, USA</p>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center text-sm text-gray-600">
          <p>© 2024 DiaWise. All rights reserved.</p>
          <p className="mt-2">
            <a href="/privacy-policy" className="text-blue-600 hover:underline">Privacy Policy</a>
            {' • '}
            <a href="/cookie-policy" className="text-blue-600 hover:underline">Cookie Policy</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
